import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface JVReportFilters {
  subsidiary_ids?: (number | string)[];
  account_id?: number | string;
  location_id?: number | string;
  line_of_business_id?: number | string;
  from_date?: string; // YYYY-MM-DD
  to_date?: string; // YYYY-MM-DD
  limit?: number;
  offset?: number;
  exclude_void?: boolean;
}

export interface JVReportRow {
  subsidiary: string;
  jv_date: string;
  jv_no: string;
  gl_code: string;
  gl_account_name: string;
  customer_vendor_code: string;
  customer_vendor_name: string;
  debit_amount: number;
  credit_amount: number;
  memo: string;
  description: string;
  line_of_business: string;
  location: string;
  journal_entry_id: number;
  journal_entry_line_id: number;
}

export interface JVReportResponse {
  status: string;
  message: string;
  data: {
    rows: JVReportRow[];
    total_rows: number;
    total_pages: number;
    total_debit: number;
    total_credit: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class JournalVoucherReportService {
  private readonly API_BASE_URL = environment.accountingUrl;
  private http = inject(HttpClient);

  /**
   * Get journal voucher report with optional filters
   * Endpoint: GET /api/v1/reports/journal-voucher
   */
  getJVReport(filters: JVReportFilters): Observable<JVReportResponse> {
    let params = new HttpParams();

    if (filters.subsidiary_ids?.length) {
      params = params.set(
        'subsidiary_ids',
        filters.subsidiary_ids.join(',')
      );
    }

    if (filters.account_id !== undefined && filters.account_id !== null) {
      params = params.set('account_id', filters.account_id.toString());
    }

    if (filters.location_id !== undefined && filters.location_id !== null) {
      params = params.set('location_id', filters.location_id.toString());
    }

    if (filters.line_of_business_id !== undefined && filters.line_of_business_id !== null) {
      params = params.set('line_of_business_id', filters.line_of_business_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get<JVReportResponse>(`${this.API_BASE_URL}/api/v1/reports/journal-voucher`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Export journal voucher report to Excel
   * Endpoint: GET /api/v1/reports/journal-voucher/export?format=xlsx
   */
  exportToExcel(filters: JVReportFilters): Observable<Blob> {
    let params = this.buildExportParams(filters);
    params = params.set('format', 'xlsx');

    return this.http.get(`${this.API_BASE_URL}/api/v1/reports/journal-voucher/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }

  /**
   * Export journal voucher report to PDF
   * Endpoint: GET /api/v1/reports/journal-voucher/export?format=pdf
   */
  exportToPdf(filters: JVReportFilters): Observable<Blob> {
    let params = this.buildExportParams(filters);
    params = params.set('format', 'pdf');

    return this.http.get(`${this.API_BASE_URL}/api/v1/reports/journal-voucher/export`, {
      params,
      responseType: 'blob',
      withCredentials: true
    });
  }

  private buildExportParams(filters: JVReportFilters): HttpParams {
    let params = new HttpParams();

    if (filters.subsidiary_ids?.length) {
      params = params.set(
        'subsidiary_ids',
        filters.subsidiary_ids.join(',')
      );
    }

    if (filters.account_id !== undefined && filters.account_id !== null) {
      params = params.set('account_id', filters.account_id.toString());
    }

    if (filters.location_id !== undefined && filters.location_id !== null) {
      params = params.set('location_id', filters.location_id.toString());
    }

    if (filters.line_of_business_id !== undefined && filters.line_of_business_id !== null) {
      params = params.set('line_of_business_id', filters.line_of_business_id.toString());
    }

    if (filters.from_date) {
      params = params.set('from_date', filters.from_date);
    }

    if (filters.to_date) {
      params = params.set('to_date', filters.to_date);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return params;
  }
}
